import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { LobbyChannel, LobbyMessage, Profile } from '@/types/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { profanityFilter } from '@/lib/profanityFilter';
import { useProfile } from './useProfile';
import { EmojiClickData } from 'emoji-picker-react'; // Import EmojiClickData

const MESSAGE_BATCH_SIZE = 50;
const MAX_MESSAGES_PER_INTERVAL = 5; // Max messages allowed
const MESSAGE_INTERVAL_MS = 5000; // Within 5 seconds

export function useLobby(channelSlug: string) {
  const { user } = useAuth();
  const { profile } = useProfile(); // This is where profile is obtained
  const { toast } = useToast();
  const [channels, setChannels] = useState<LobbyChannel[]>([]);
  const [activeChannel, setActiveChannel] = useState<LobbyChannel | null>(null);
  const [messages, setMessages] = useState<LobbyMessage[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set()); // Corrected this line
  const [channelMembers, setChannelMembers] = useState<Profile[]>([]);
  
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [messagePage, setMessagePage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const userMessageTimestampsRef = useRef<Map<string, number[]>>(new Map());

  useEffect(() => {
    async function fetchChannels() {
      setLoadingChannels(true);
      const { data, error } = await supabase.from('lobby_channels').select('*').order('name', { ascending: true });
      if (error) {
        console.error('Error fetching channels:', error);
        toast({ title: 'Error', description: 'Could not fetch discussion channels.', variant: 'destructive' });
        setChannels([]);
      } else {
        setChannels(data as LobbyChannel[]);
      }
      setLoadingChannels(false);
    }
    fetchChannels();
  }, [toast]);

  useEffect(() => {
    if (channels.length > 0) {
      const channel = channels.find(c => c.slug === channelSlug) || channels[0];
      setActiveChannel(channel);
    }
  }, [channels, channelSlug]);

  const fetchMessagesWithProfiles = useCallback(async (channelId: string, page: number) => {
    const from = page * MESSAGE_BATCH_SIZE;
    const to = from + MESSAGE_BATCH_SIZE - 1;

    const { data: messagesData, error: messagesError } = await supabase
      .from('lobby_messages')
      .select('*, reactions') // Explicitly select reactions
      .eq('channel_id', channelId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (messagesError) {
      console.error('Error fetching messages:', messagesError);
      toast({ title: 'Error', description: 'Could not fetch messages.', variant: 'destructive' });
      return [];
    }

    if (!messagesData || messagesData.length < MESSAGE_BATCH_SIZE) {
      setHasMoreMessages(false);
    }
    
    if (!messagesData || messagesData.length === 0) {
        return [];
    }

    const userIds = [...new Set(messagesData.map(m => m.user_id))];
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .in('id', userIds);

    if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
    }

    const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

    const messagesWithProfiles = messagesData.map(message => ({
      ...message,
      profiles: profilesMap.get(message.user_id) || null
    }));

    return messagesWithProfiles.reverse();
  }, [toast]);

  useEffect(() => {
    if (!activeChannel) return;

    const initialLoad = async () => {
      setLoadingMessages(true);
      setMessages([]);
      setMessagePage(0);
      setHasMoreMessages(true);
      const newMessages = await fetchMessagesWithProfiles(activeChannel.id, 0);
      setMessages(newMessages as any[]);
      setLoadingMessages(false);
    };

    initialLoad();
  }, [activeChannel, fetchMessagesWithProfiles]);

  const loadMoreMessages = useCallback(async () => {
    if (loadingMoreMessages || !hasMoreMessages || !activeChannel) return;

    setLoadingMoreMessages(true);
    const nextPage = messagePage + 1;
    const newMessages = await fetchMessagesWithProfiles(activeChannel.id, nextPage);
    
    setMessages(prevMessages => [...newMessages, ...prevMessages]);
    setMessagePage(nextPage);
    setLoadingMoreMessages(false);
  }, [activeChannel, fetchMessagesWithProfiles, hasMoreMessages, loadingMoreMessages, messagePage]);

  useEffect(() => {
    if (!activeChannel || !user) return;

    const fetchMembers = async () => {
      let profiles: Profile[] = [];
      if (activeChannel.is_private) {
        // For private channels, fetch only explicit members
        const { data, error } = await supabase.from('lobby_channel_members').select('profiles(id, full_name, avatar_url, username, callsign)').eq('channel_id', activeChannel.id);
        if (error) {
          console.error('Error fetching private channel members:', error);
          setChannelMembers([]);
          return;
        }
        profiles = data.map(m => m.profiles).filter(Boolean) as Profile[];
      } else {
        // For public channels, fetch all profiles (excluding current user)
        const { data, error } = await supabase.from('profiles').select('id, full_name, avatar_url, username, callsign').neq('id', user.id);
        if (error) {
          console.error('Error fetching all profiles for public channel:', error);
          setChannelMembers([]);
          return;
        }
        profiles = data as Profile[];
      }
      setChannelMembers(profiles);
    };
    fetchMembers();

    const messageChannel = supabase.channel(`lobby:${activeChannel.id}`);
    messageChannel
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'lobby_messages', filter: `channel_id=eq.${activeChannel.id}` },
        async (payload) => {
          const newMessage = payload.new as LobbyMessage;
          const { data: profileData } = await supabase.from('profiles').select('full_name, avatar_url').eq('id', newMessage.user_id).single();
          newMessage.profiles = profileData;
          setMessages((prevMessages) => [...prevMessages, newMessage]);
        }
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'lobby_messages', filter: `channel_id=eq.${activeChannel.id}` },
        (payload) => {
          const updatedMessage = payload.new as LobbyMessage;
          setMessages(prevMessages => {
            const existingMessage = prevMessages.find(m => m.id === updatedMessage.id);
            if (existingMessage) {
              updatedMessage.profiles = existingMessage.profiles;
            }
            return prevMessages.map(m =>
              m.id === updatedMessage.id ? updatedMessage : m
            );
          });
        }
      )
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'lobby_messages', filter: `channel_id=eq.${activeChannel.id}` },
        (payload) => {
          setMessages((prevMessages) => prevMessages.filter(m => m.id !== payload.old.id));
        }
      )
      .subscribe();

    const presenceChannel = supabase.channel(`lobby-presence:${activeChannel.id}`);
    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const userIds = new Set<string>();
        for (const id in presenceChannel.presenceState()) {
          (presenceChannel.presenceState()[id] as any[]).forEach(p => userIds.add(p.user_id));
        }
        setOnlineUsers(userIds);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({ user_id: user.id, online_at: new Date().toISOString() });
        }
      });

    return () => {
      supabase.removeChannel(messageChannel);
      supabase.removeChannel(presenceChannel);
    };
  }, [activeChannel, user]);

  const sendMessage = useCallback(async (content: string, files: File[] = []) => {
    if (!user || !activeChannel || (!content.trim() && files.length === 0)) return;
  
    let messageContent = content.trim();
    const userId = user.id;
    const currentTime = Date.now();
  
    // --- rate limiting ---
    const userTimestamps = userMessageTimestampsRef.current.get(userId) || [];
    const recentTimestamps = userTimestamps.filter(
      (timestamp) => currentTime - timestamp < MESSAGE_INTERVAL_MS
    );
    if (recentTimestamps.length >= MAX_MESSAGES_PER_INTERVAL) {
      toast({
        title: "Too Many Messages",
        description: `Please wait a moment before sending another message. You can send ${MAX_MESSAGES_PER_INTERVAL} messages every ${
          MESSAGE_INTERVAL_MS / 1000
        } seconds.`,
        variant: "destructive",
      });
      return;
    }
    userMessageTimestampsRef.current.set(userId, [...recentTimestamps, currentTime]);
  
    try {
      if (profanityFilter.isProfane(messageContent)) {
        toast({
          title: "Message Blocked",
          description: "Your message contains inappropriate content and could not be sent.",
          variant: "destructive",
        });
        return;
      }
  
      // --- Handle file uploads ---
      if (files.length > 0) {
        const uploadPromises = files.map(async (file) => {
          const fileName = `${user.id}/${Date.now()}-${file.name}`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('lobby-uploads')
            .upload(fileName, file);

          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            throw new Error(`Failed to upload ${file.name}.`);
          }

          const { data: urlData } = supabase.storage
            .from('lobby-uploads')
            .getPublicUrl(uploadData.path);
          
          return `![${file.name}](${urlData.publicUrl})`;
        });

        const imageUrls = await Promise.all(uploadPromises);
        messageContent = `${messageContent}\n${imageUrls.join('\n')}`.trim();
      }

      // --- extract mentions ---
      const mentionRegex = /@(\w+)/g;
      const mentionedUsernames: string[] = [];
      let match;
      while ((match = mentionRegex.exec(messageContent)) !== null) {
        mentionedUsernames.push(match[1]);
      }

      // Resolve usernames to user IDs from channelMembers
      const mentionedUserIds = channelMembers
        .filter(member => member.username && mentionedUsernames.includes(member.username))
        .map(member => member.id);
  
      const payload = {
        content: messageContent,
        user_id: user.id,
        channel_id: activeChannel.id,
        mentions: mentionedUserIds, // Now an array of user UUIDs
        reactions: {},
      };
  
      console.log("Final message payload before insert:", payload);
  
      const { error: insertError } = await supabase
        .from("lobby_messages")
        .insert(payload);
  
      if (insertError) {
        console.error("Error sending message:", insertError);
        throw new Error("Could not send message.");
      }
    } catch (error: any) {
      toast({
        title: "Message Not Sent",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [user, activeChannel, toast, channelMembers]);
  

  const editMessage = useCallback(async (messageId: string, content: string) => {
    if (!user || !content.trim()) return;

    const trimmedContent = content.trim();
    if (profanityFilter.isProfane(trimmedContent)) {
      toast({
        title: 'Message Blocked',
        description: 'Your message contains inappropriate content and could not be sent.',
        variant: 'destructive',
      });
      return;
    }

    const { error } = await supabase
      .from('lobby_messages')
      .update({ content: trimmedContent, edited_at: new Date().toISOString() })
      .eq('id', messageId);

    if (error) {
      console.error('Error updating message:', error);
      toast({ title: 'Error', description: 'Could not update message.', variant: 'destructive' });
    }
  }, [user, toast]);

  const deleteMessage = useCallback(async (messageId: string) => {
    const { error } = await supabase
      .from('lobby_messages')
      .delete()
      .eq('id', messageId);

    if (error) {
      console.error('Error deleting message:', error);
      toast({ title: 'Error', description: 'Could not delete message.', variant: 'destructive' });
    }
  }, [toast]);

  const toggleReaction = useCallback(async (messageId: string, emojiData: EmojiClickData) => {
    if (!user) return;
    const emoji = emojiData.emoji;
    const imageUrl = emojiData.imageUrl;

    try {
      const { data, error } = await supabase
        .rpc('toggle_lobby_message_reaction', {
          p_message_id: messageId,
          p_emoji: emoji,
          p_image_url: imageUrl || ''
        });

      if (error) {
        console.error('Error toggling reaction via RPC:', error);
        toast({ title: 'Error', description: 'Could not add reaction.', variant: 'destructive' });
        return;
      }
      // Optional: optimistic UI update if needed; realtime will also push changes
      setMessages(prev =>
        prev.map(m => (m.id === messageId ? { ...m, reactions: data || m.reactions } : m))
      );
    } catch (e: any) {
      console.error('Toggle reaction failed:', e);
      toast({ title: 'Error', description: e.message || 'Could not add reaction.', variant: 'destructive' });
    }
  }, [user, toast, setMessages]);

  // Add this log to check the profile object returned by useProfile
  useEffect(() => {
    if (profile) {
    }
  }, [profile]);

  return {
    channels,
    activeChannel,
    messages,
    loadingChannels,
    loadingMessages,
    sendMessage,
    channelMembers,
    onlineUsers,
    loadMoreMessages,
    hasMoreMessages,
    loadingMoreMessages,
    toggleReaction,
    deleteMessage,
    editMessage,
    profile, // This is the profile being returned
  };
}